/**
 * Tiny colour helpers for the configurator preview. Brand tokens arrive as plain
 * CSS colour strings (hex or rgb/rgba), so this parses them just enough to make a
 * light-vs-dark decision (e.g. which ink keeps a loader legible on a brand fill).
 */

/** Parse a `#rgb` / `#rgba` / `#rrggbb` / `#rrggbbaa` or `rgb()/rgba()` colour to
 *  `[r, g, b]` (0-255), or `null` if it isn't one of those forms. */
function parseRgb(color: string): [number, number, number] | null {
  const c = color.trim();

  const hex = c.match(/^#([0-9a-f]{3,8})$/i);
  if (hex) {
    let h = hex[1];
    if (h.length === 3 || h.length === 4) {
      h = h
        .split('')
        .map((x) => x + x)
        .join('');
    }
    if (h.length < 6) return null;
    return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
  }

  const rgb = c.match(/^rgba?\(([^)]+)\)$/i);
  if (rgb) {
    const parts = rgb[1].split(/[,/\s]+/).map((s) => parseFloat(s));
    if (parts.length >= 3 && parts.slice(0, 3).every((n) => Number.isFinite(n))) {
      return [parts[0], parts[1], parts[2]];
    }
  }

  return null;
}

/**
 * True when a colour is light enough that white ink on it would read poorly, so a
 * dark treatment should be used instead. Uses the perceptual luma weighting; an
 * unparseable colour returns `false` (assume a dark surface → keep white ink).
 */
export function isLightColor(color: string | undefined): boolean {
  if (!color) return false;
  const rgb = parseRgb(color);
  if (!rgb) return false;
  const [r, g, b] = rgb;
  const luma = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luma > 0.6;
}

/**
 * Rejects a CSS paint VALUE (colour or background) that could break out of its
 * `property: value;` declaration once interpolated raw into a styled-components
 * template (e.g. `background: ${value};`). These tokens are brand config from the
 * CMS/backend and support real complexity beyond a plain colour (multi-stop
 * gradients, positions, sizes), so this does not try to parse/whitelist the value's
 * shape — it only rejects the handful of characters a legitimate CSS paint value
 * never needs: `{` / `}` (would close the current rule and open a new one) and `;`
 * (would end the declaration early). Returns `fallback` if the value looks unsafe,
 * else the original value unchanged.
 */
export function sanitizeCssPaint(value: string, fallback: string): string {
  return /[{};]/.test(value) ? fallback : value;
}
